import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/i18n";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user !== false) {
      navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(data.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-rooftop)] px-6 py-12">
      <div className="absolute inset-0 -z-0 opacity-30">
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-gold/5 blur-3xl" />
      </div>
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="block text-center mb-14 group" data-testid="login-logo">
          <span className="font-heading font-extralight text-[1.7rem] sm:text-[2rem] tracking-[0.55em] text-[var(--text-primary)] group-hover:text-[var(--gold)] transition-colors duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]">
            SONARA
          </span>
        </Link>
        <div className="surface-1 rounded-md p-8 animate-fade-up">
          <h1 className="font-heading text-2xl font-light mb-2" data-testid="login-title">{t("auth.login.title")}</h1>
          <p className="text-sm text-[var(--text-muted)] mb-8">{t("auth.login.subtitle")}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-2 block">{t("common.email")}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-4 h-11 text-sm focus:outline-none focus:ring-1 focus:ring-gold focus:border-transparent transition-all"
                placeholder="you@restaurant.com"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-2 block">{t("common.password")}</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-4 h-11 text-sm focus:outline-none focus:ring-1 focus:ring-gold focus:border-transparent transition-all"
              />
            </div>
            {error && <div className="text-xs text-red-400" data-testid="login-error">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t("common.loading") : t("auth.login.submit")}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-[var(--text-dim)]">
            <div className="flex-1 h-px bg-white/5" />
            <span>{t("common.or")}</span>
            <div className="flex-1 h-px bg-white/5" />
          </div>

          <button
            onClick={handleGoogle}
            data-testid="login-google-button"
            className="w-full btn-ghost text-sm flex items-center justify-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.5l-6.5-5.3C29.6 35 26.9 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.6 39.7 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.7l6.5 5.3C41.9 35 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
            {t("auth.login.google")}
          </button>

          <p className="text-xs text-[var(--text-muted)] text-center mt-8">
            {t("auth.login.no_account")}{" "}
            <Link to="/register" className="text-gold hover:text-gold-light" data-testid="login-register-link">
              {t("auth.login.register_link")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
